import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  UilCalendarAlt, UilCheckCircle, UilStar, UilShieldCheck,
  UilHome, UilBuilding, UilBed, UilArrowRight, UilPhone,
} from '@iconscout/react-unicons';
import CleanHome from '../assets/Images/CleanHome.jpg';
import Clean     from '../assets/Images/clean.jpg';
import Office    from '../assets/Images/office.jpeg';
import CleanKitchen from '../assets/Images/CleanKitchen.jpg';
import Request   from '../components/Request';
import FooterPage from '../components/FooterPage';

const WHY = [
  { icon: <UilStar size={28} />,        title: 'Highly Rated',       description: '5.0 stars on Google & Yelp',   color: 'bg-amber-50 text-amber-600' },
  { icon: <UilShieldCheck size={28} />, title: 'Licensed & Insured', description: 'Fully bonded for your protection', color: 'bg-blue-50 text-blue-600' },
  { icon: <UilCheckCircle size={28} />, title: '25+ Years Experience', description: 'Family owned since 1998',     color: 'bg-brand-50 text-brand-600' },
];

const serviceDetails = [
  {
    id: 'home', title: 'Home Cleaning', img: Clean,        icon: <UilHome size={32} />,
    text: 'Our team arrives fully equipped to clean every room, living space, kitchen, and bathroom — guaranteed to leave your home sparkling and refreshed.',
    features: ['Deep cleaning of all rooms', 'Kitchen & bathroom sanitization', 'Flexible scheduling', 'Eco-friendly products'],
    badge: 'Most Popular', badgeColor: 'bg-brand-500',
  },
  {
    id: 'airbnb', title: 'Airbnb Cleaning', img: CleanHome, icon: <UilBed size={32} />,
    text: 'Stress-free turnovers: we handle linen changes, deep cleans, restocking essentials, and rapid guest-ready touch-ups between bookings.',
    features: ['Fast turnaround times', 'Linen service available', 'Guest-ready guarantee', 'Same-day service options'],
    badge: 'Fast Turnaround', badgeColor: 'bg-rose-500',
  },
  {
    id: 'office', title: 'Office Cleaning', img: Office,    icon: <UilBuilding size={32} />,
    text: 'From lobbies to conference rooms, we deliver a pristine, professional workspace for employees and clients alike.',
    features: ['After-hours availability', 'Conference room prep', 'Break room cleaning', 'Professional appearance'],
    badge: 'Commercial', badgeColor: 'bg-violet-500',
  },
  {
    id: 'movein', title: 'Move In/Out Cleaning', img: CleanKitchen, icon: <UilCalendarAlt size={32} />,
    text: 'Ensure a smooth transition with our comprehensive move in/out cleaning services, tailored to meet the needs of new and departing residents.',
    features: ['Top to bottom cleaning', 'Appliance cleaning', 'Cabinet & closet cleaning', 'Ready for inspection'],
    badge: 'Comprehensive', badgeColor: 'bg-emerald-500',
  },
];

export default function Services() {
  return (
    <div className="bg-white text-slate-900">
      <Helmet>
        <title>Cleaning Services Seattle & Eastside | Home, Office, Airbnb Cleaning</title>
        <meta name="description" content="Professional cleaning services: Home cleaning, deep cleaning, office cleaning, Airbnb cleaning, move in/out services in Seattle, Bellevue, Redmond. Call (425) 829-6092." />
        <link rel="canonical" href="https://fabycleaners.com/services" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-end bg-cover bg-center" style={{ backgroundImage: `url(${CleanHome})` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-slate-900/20" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center text-white">
            <span className="inline-block bg-gold-500/20 border border-gold-400/30 text-gold-300 text-xs font-semibold px-4 py-2 rounded-full mb-5">
              Professional Cleaning · Seattle & Eastside
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-5 leading-tight">Our Premium Services</h1>
            <p className="text-white/65 text-xl max-w-2xl mx-auto mb-8">Experience the art of immaculate living with our comprehensive cleaning solutions.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:425-829-6092"
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold px-7 py-3.5 rounded-xl transition-colors">
                <UilPhone size={18} /> Call (425) 829-6092
              </a>
              <a href="#services"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold px-7 py-3.5 rounded-xl transition-all">
                View Services <UilArrowRight size={18} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-3 gap-6">
            {WHY.map((w, idx) => (
              <motion.div key={w.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-7 border border-slate-100 shadow-card flex items-start gap-5">
                <div className={`w-14 h-14 ${w.color} rounded-2xl flex items-center justify-center shrink-0`}>{w.icon}</div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{w.title}</h3>
                  <p className="text-sm text-slate-500">{w.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="inline-block bg-brand-50 text-brand-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Tailored Cleaning Solutions</h2>
            <p className="text-slate-500 max-w-xl mx-auto">Professional cleaning for every need — from weekly maintenance to one-time deep cleans.</p>
          </motion.div>

          <div className="space-y-20">
            {serviceDetails.map(({ id, title, img, icon, text, features, badge, badgeColor }, idx) => (
              <motion.div key={id}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}
              >
                {/* Image */}
                <div className="lg:w-1/2 w-full">
                  <div className="relative group">
                    <img src={img} alt={title} className="w-full h-80 lg:h-96 object-cover rounded-3xl" />
                    <div className={`absolute top-4 ${idx % 2 === 0 ? 'right-4' : 'left-4'} ${badgeColor} text-white text-xs font-bold px-3 py-1.5 rounded-xl`}>
                      {badge}
                    </div>
                    <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-brand-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
                      {icon}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2 w-full">
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5">{title}</h2>
                  <p className="text-slate-600 leading-relaxed mb-8 text-lg">{text}</p>
                  <div className="grid sm:grid-cols-2 gap-3 mb-8">
                    {features.map((feat) => (
                      <div key={feat} className="flex items-center gap-3 bg-brand-50 rounded-xl px-4 py-3">
                        <UilCheckCircle size={16} className="text-brand-600 shrink-0" />
                        <span className="text-sm font-medium text-slate-700">{feat}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <a href="tel:425-829-6092"
                      className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors text-sm">
                      <UilCalendarAlt size={18} /> Schedule Today
                    </a>
                    <Link to="/request"
                      className="inline-flex items-center gap-2 border-2 border-brand-600 text-brand-700 hover:bg-brand-50 font-semibold px-6 py-3 rounded-xl transition-colors text-sm">
                      Get Free Quote <UilArrowRight size={18} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-bold text-white mb-5">Ready for a Spotless Space?</h2>
            <p className="text-slate-400 text-lg mb-10">Get your free, no-obligation quote today and experience the Fabiana difference.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:425-829-6092"
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold px-8 py-4 rounded-xl transition-colors">
                <UilPhone size={20} /> Call (425) 829-6092
              </a>
              <Link to="/request"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold px-8 py-4 rounded-xl transition-all">
                Request Free Quote <UilArrowRight size={20} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Request />
      <FooterPage />
    </div>
  );
}
